<template>
  <div class="content">
    <div class="container-fluid">
      <div class="row justify-content-center">
        <div class="col-12">
          <b-card no-body class="network-card">
            <b-card-header>
              <b-row align-v="center">
                <b-col md="4">
                  <h4 class="mb-0">Your CloudAM Network</h4>
                </b-col>
                <b-col md="4">
                  <b-input-group>
                    <b-form-input 
                      v-model="searchQuery" 
                      placeholder="Search nodes..." 
                      @input="filterNodes"
                    ></b-form-input>
                    <b-input-group-append>
                      <b-button variant="outline-primary">
                        <i class="fa fa-search"></i>
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                </b-col>
                <b-col md="4" class="text-right">
                  <b-button-group>
                    <b-button 
                      v-for="option in layoutOptions" 
                      :key="option.value"
                      :variant="selectedLayout === option.value ? 'primary' : 'outline-primary'"
                      @click="selectedLayout = option.value"
                    >
                      {{ option.text }}
                    </b-button>
                  </b-button-group>
                </b-col>
              </b-row>
            </b-card-header>
            <b-card-body class="p-0">
              <div v-if="networkDown" class="network-down">
                <div class="fm-empty network-empty">
                  <div class="network-empty-title">The Cloud AM network is not ready</div>
                  <div class="network-empty-desc">Please activate at least one agent to view your network.</div>
                  <b-button variant="primary" size="sm" to="/agents" class="mt-3">Go to Agents</b-button>
                </div>
              </div>
              <div v-else class="network-interface">
                <div ref="graphContainer" class="graph-container">
                  <div id="d3-graph"></div>
                </div>
                <b-sidebar v-model="showDetails" right title="Node Details" shadow>
                  <div v-if="nodeDetails" class="fm-node-panel">
                    <div class="fm-node-head">
                      <img :src="getIconForGroup(nodeDetails.group)" width="40" height="40" class="fm-node-icon">
                      <div class="fm-node-title fm-mono">{{ nodeDetails.id }}</div>
                      <span class="fm-status-pill fm-status-pill--neu">{{ nodeDetails.group }}</span>
                    </div>

                    <div class="fm-node-section-label">Properties</div>
                    <div class="fm-node-fields">
                      <div class="fm-field" v-for="(value, key) in nodeDetails" :key="key" v-if="key !== 'id' && key !== 'group' && key !== 'x' && key !== 'y' && key !== 'fx' && key !== 'fy' && key !== 'vx' && key !== 'vy' && key !== 'index'">
                        <span class="fm-field-label">{{ key }}</span>
                        <span class="fm-field-value fm-mono">{{ value }}</span>
                      </div>
                    </div>

                    <div class="fm-node-section-label">Connected Nodes</div>
                    <div class="fm-node-conns">
                      <button type="button" class="fm-node-conn" v-for="connection in getConnections(nodeDetails)" :key="connection.id" @click="selectNode(connection)">
                        <span class="fm-mono">{{ connection.id }}</span>
                        <span class="fm-status-pill fm-status-pill--neu">{{ connection.group }}</span>
                      </button>
                    </div>

                    <div class="fm-node-foot">
                      <b-button
                        variant="outline-primary"
                        size="sm"
                        block
                        :to="getActionLink(nodeDetails)"
                      >
                        View Details
                      </b-button>
                    </div>
                  </div>
                </b-sidebar>
              </div>
            </b-card-body>
          </b-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3';
import apiClient from "@/http";

export default {
  data() {
    return {
      networkData: null,
      agents: [],
      services: [],
      users: [],
      clients: [],
      networkDown: false,
      selectedLayout: 'force',
      selectedNode: null,
      simulation: null,
      searchQuery: '',
      showDetails: false,
      nodeDetails: null,
      layoutOptions: [
        { value: 'force', text: 'Network View' },
        { value: 'compact', text: 'Compact View' }
      ]
    };
  },
  watch: {
    selectedLayout() {
      this.updateVisualization();
    }
  },
  mounted() {
    this.fetchNetworkData();
    window.addEventListener('resize', this.handleResize);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    if (this.simulation) {
      this.simulation.stop();
    }
  },
  methods: {
    fixCloudAmCenter(nodes, width, height) {
      const cloudAm = nodes.find(n => n.id === 'cloud-am');
      if (cloudAm) {
        cloudAm.fx = width / 2;
        cloudAm.fy = height / 2;
        return cloudAm;
      }
      return null;
    },
    async fetchNetworkData() {
      try {
        const response = await apiClient.get('/sessions/network');
        this.networkData = response.data;
        this.agents = this.networkData.Nodes.filter(node => node.group === 'agent');
        this.services = this.networkData.Nodes.filter(node => node.group === 'service');
        this.users = this.networkData.Nodes.filter(node => node.group === 'user');
        this.clients = this.networkData.Nodes.filter(node => node.group === 'client');
        this.updateVisualization();
      } catch (error) {
        console.error('Error fetching network data:', error);
        this.networkDown = true;
      }
    },

    handleResize() {
      this.updateVisualization();
    },

    findPath(source, target, links) {
      const graph = new Map();
      links.forEach(link => {
        if (!graph.has(link.source.id)) graph.set(link.source.id, []);
        if (!graph.has(link.target.id)) graph.set(link.target.id, []);
        graph.get(link.source.id).push(link.target.id);
        graph.get(link.target.id).push(link.source.id);
      });

      const queue = [[source.id]];
      const visited = new Set([source.id]);

      while (queue.length > 0) {
        const path = queue.shift();
        const node = path[path.length - 1];

        if (node === target.id) {
          return path;
        }

        for (const neighbor of graph.get(node) || []) {
          if (!visited.has(neighbor)) {
            visited.add(neighbor);
            queue.push([...path, neighbor]);
          }
        }
      }
      return null;
    },

    filterNodes() {
      this.updateVisualization();
    },
    
    getConnections(node) {
      if (!this.networkData || !node) return [];
      
      const connections = [];
      this.networkData.Links.forEach(link => {
        if (link.source.id === node.id) {
          connections.push(link.target);
        } else if (link.target.id === node.id) {
          connections.push(link.source);
        }
      });
      
      return connections;
    },
    
    selectNode(node) {
      this.nodeDetails = node;
      this.showDetails = true;
      this.selectedNode = node;
      this.highlightPaths(node, this.networkData.Links, 
        d3.select("#d3-graph").selectAll("line"), 
        d3.select("#d3-graph").selectAll("g.node"));
    },
    
    getVariantForGroup(group) {
      const variants = {
        'agent': 'success',
        'service': 'info',
        'user': 'primary',
        'client': 'warning',
        'server': 'secondary'
      };
      return variants[group] || 'secondary';
    },
    
    getActionLink(node) {
      if (!node) return '/';
      
      switch (node.group) {
        case 'agent':
          return '/agents';
        case 'service':
          return '/services';
        case 'user':
          return '/users';
        case 'client':
          return '/resources';
        default:
          return '/';
      }
    },
    
    updateVisualization() {
      const container = this.$refs.graphContainer;
      if (!container || !this.networkData) return;

      d3.select("#d3-graph").selectAll("*").remove();

      const width = container.clientWidth;
      const height = Math.max(window.innerHeight * 0.8, 600);

      // Create a gradient background
      const svg = d3.select("#d3-graph")
        .append("svg")
        .attr("width", width)
        .attr("height", height)
        .on("click", (event) => {
          // If clicking on the background (not a node), clear selection
          if (event.target.tagName === 'svg' || event.target.tagName === 'rect') {
            this.selectedNode = null;
            this.showDetails = false;
            this.highlightPaths(null, links, d3.selectAll("path"), d3.selectAll("g.node"));
          }
        });
        
      // Add a subtle gradient background
      const defs = svg.append("defs");
      const gradient = defs.append("linearGradient")
        .attr("id", "network-gradient")
        .attr("x1", "0%")
        .attr("y1", "0%")
        .attr("x2", "100%")
        .attr("y2", "100%");
        
      gradient.append("stop")
        .attr("offset", "0%")
        .attr("stop-color", "#f0f4f8");
        
      gradient.append("stop")
        .attr("offset", "100%")
        .attr("stop-color", "#e9f2f9");
        
      svg.append("rect")
        .attr("width", width)
        .attr("height", height)
        .attr("fill", "url(#network-gradient)");

      const g = svg.append("g");

      // Add zoom capabilities
      const zoom = d3.zoom()
        .scaleExtent([0.1, 4])
        .on("zoom", (event) => g.attr("transform", event.transform));
        
      svg.call(zoom);
      
      // Add controls for zoom
      const zoomControls = svg.append("g")
        .attr("class", "zoom-controls")
        .attr("transform", `translate(${width - 50}, 20)`);
        
      zoomControls.append("circle")
        .attr("r", 15)
        .attr("fill", "#fff")
        .attr("stroke", "#ddd")
        .attr("cx", 0)
        .attr("cy", 0)
        .style("cursor", "pointer")
        .on("click", () => {
          svg.transition().duration(500).call(zoom.scaleBy, 1.3);
        });
        
      zoomControls.append("text")
        .attr("text-anchor", "middle")
        .attr("dominant-baseline", "central")
        .attr("x", 0)
        .attr("y", 0)
        .text("+")
        .style("font-size", "18px")
        .style("pointer-events", "none");
        
      zoomControls.append("circle")
        .attr("r", 15)
        .attr("fill", "#fff")
        .attr("stroke", "#ddd")
        .attr("cx", 0)
        .attr("cy", 40)
        .style("cursor", "pointer")
        .on("click", () => {
          svg.transition().duration(500).call(zoom.scaleBy, 0.7);
        });
        
      zoomControls.append("text")
        .attr("text-anchor", "middle")
        .attr("dominant-baseline", "central")
        .attr("x", 0)
        .attr("y", 40)
        .text("-")
        .style("font-size", "18px")
        .style("pointer-events", "none");
        
      zoomControls.append("circle")
        .attr("r", 15)
        .attr("fill", "#fff")
        .attr("stroke", "#ddd")
        .attr("cx", 0)
        .attr("cy", 80)
        .style("cursor", "pointer")
        .on("click", () => {
          svg.transition().duration(750).call(zoom.transform, d3.zoomIdentity);
        });
        
      zoomControls.append("text")
        .attr("text-anchor", "middle")
        .attr("dominant-baseline", "central")
        .attr("x", 0)
        .attr("y", 80)
        .text("⟲")
        .style("font-size", "12px")
        .style("pointer-events", "none");

      // Filter nodes based on search query
      let filteredNodes = this.networkData.Nodes;
      let filteredLinks = this.networkData.Links;
      
      if (this.searchQuery) {
        const lowercaseQuery = this.searchQuery.toLowerCase();
        filteredNodes = this.networkData.Nodes.filter(node => 
          node.id.toLowerCase().includes(lowercaseQuery) || 
          node.group.toLowerCase().includes(lowercaseQuery)
        );
        
        // Keep only links where both source and target are in filtered nodes
        const nodeIds = new Set(filteredNodes.map(n => n.id));
        filteredLinks = this.networkData.Links.filter(link => 
          nodeIds.has(link.source.id || link.source) && 
          nodeIds.has(link.target.id || link.target)
        );
      }
      
      const nodes = filteredNodes.map(node => ({...node}));
      const links = filteredLinks.map(link => ({...link}));

      // Create forces based on selected layout
      if (this.simulation) this.simulation.stop();

      this.simulation = d3.forceSimulation(nodes);

      switch (this.selectedLayout) {
        case 'compact':
          this.fixCloudAmCenter(nodes, width, height);
          this.simulation
            .force("link", d3.forceLink(links).id(d => d.id).distance(80))
            .force("charge", d3.forceManyBody().strength(-400))
            .force("center", d3.forceCenter(width / 2, height / 2))
            .force("x", d3.forceX(width / 2).strength(0.1))
            .force("y", d3.forceY(height / 2).strength(0.1))
            .force("collide", d3.forceCollide().radius(40));
          break;

        default: // force
          this.fixCloudAmCenter(nodes, width, height);
          this.simulation
            .force("link", d3.forceLink(links).id(d => d.id).distance(120))
            .force("charge", d3.forceManyBody().strength(-800))
            .force("center", d3.forceCenter(width / 2, height / 2))
            .force("collide", d3.forceCollide().radius(60));
          break;
      }

      // Create link group
      const linkGroup = g.append("g").attr("class", "links");
      
      // Create links with curved paths for better visibility
      const link = linkGroup
        .selectAll("path")
        .data(links)
        .enter()
        .append("path")
        .attr("stroke", "#ccd6e0")
        .attr("stroke-width", 2)
        .attr("fill", "none")
        .attr("marker-end", "url(#arrowhead)");
        
      // Add arrowhead marker
      defs.append("marker")
        .attr("id", "arrowhead")
        .attr("viewBox", "-0 -5 10 10")
        .attr("refX", 25)
        .attr("refY", 0)
        .attr("orient", "auto")
        .attr("markerWidth", 6)
        .attr("markerHeight", 6)
        .attr("xoverflow", "visible")
        .append("svg:path")
        .attr("d", "M 0,-4 L 8 ,0 L 0,4")
        .attr("fill", "#ccd6e0")
        .style("stroke", "none");

      // Create node group
      const nodeGroup = g.append("g").attr("class", "nodes");
      
      // Create nodes with a more appealing style
      const node = nodeGroup
        .selectAll("g")
        .data(nodes)
        .enter()
        .append("g")
        .attr("class", "node")
        .on("click", (event, d) => {
          event.stopPropagation(); // Prevent event bubbling
          
          // Stop the simulation to prevent layout shifts
          if (this.simulation) this.simulation.alphaTarget(0);
          
          this.nodeDetails = d;
          this.showDetails = true;
          this.selectedNode = this.selectedNode === d ? null : d;
          
          // Only visually highlight without affecting layout
          this.highlightPaths(d, links, link, node);
        })
        .on("mouseover", (event, d) => {
          d3.select(event.currentTarget).select("circle")
            .transition()
            .duration(200)
            .attr("r", 32);
            
          d3.select(event.currentTarget).select("text")
            .transition()
            .duration(200)
            .style("font-size", "14px")
            .style("font-weight", "bold");
        })
        .on("mouseout", (event, d) => {
          d3.select(event.currentTarget).select("circle")
            .transition()
            .duration(200)
            .attr("r", 28);
            
          d3.select(event.currentTarget).select("text")
            .transition()
            .duration(200)
            .style("font-size", "12px")
            .style("font-weight", "normal");
        });

      // Add background circles with group-specific colors
      node.append("circle")
        .attr("r", 28)
        .attr("fill", d => {
          const colors = {
            'agent': '#48BB78',
            'service': '#4299E1',
            'user': '#805AD5',
            'client': '#F6AD55',
            'server': '#CBD5E0'
          };
          return colors[d.group] || '#CBD5E0';
        })
        .attr("stroke", "#fff")
        .attr("stroke-width", 2)
        .style("cursor", "pointer")
        .style("filter", "drop-shadow(0px 2px 3px rgba(0, 0, 0, 0.1))");

      // Add icons
      node.append("image")
        .attr("xlink:href", d => this.getIconForGroup(d.group))
        .attr("width", 30)
        .attr("height", 30)
        .attr("x", -15)
        .attr("y", -15)
        .style("pointer-events", "none");

      // Add labels with better styling
      node.append("text")
        .attr("dy", 45)
        .attr("text-anchor", "middle")
        .attr("fill", "#2D3748")
        .style("font-size", "12px")
        .style("font-family", "Arial, sans-serif")
        .style("pointer-events", "none")
        .style("text-shadow", "0 1px 0 #fff, 0 -1px 0 #fff, 1px 0 0 #fff, -1px 0 0 #fff")
        .text(d => d.id);

      // Add drag behavior with smoother transitions
      node.call(d3.drag()
        .on("start", (event, d) => {
          if (!event.active) this.simulation.alphaTarget(0.3).restart();
          d.fx = d.x;
          d.fy = d.y;
        })
        .on("drag", (event, d) => {
          d.fx = event.x;
          d.fy = event.y;
        })
        .on("end", (event, d) => {
          if (!event.active) this.simulation.alphaTarget(0);
          if (this.selectedLayout === 'force') {
            d.fx = null;
            d.fy = null;
          }
        }));

      // Function to calculate path for links - using straight lines instead of curves to avoid layout issues
      const linkPath = (d) => {
        // Handle both object and primitive source/target values
        const sourceX = typeof d.source === 'object' ? d.source.x : d.x;
        const sourceY = typeof d.source === 'object' ? d.source.y : d.y;
        const targetX = typeof d.target === 'object' ? d.target.x : d.x;
        const targetY = typeof d.target === 'object' ? d.target.y : d.y;
        
        // Fallback to center if coordinates not available
        const sx = sourceX || width/2;
        const sy = sourceY || height/2;
        const tx = targetX || width/2;
        const ty = targetY || height/2;
        
        // Use a slight curve for better visibility
        const dx = tx - sx;
        const dy = ty - sy;
        
        // Use a gentler curve factor for short links
        const dist = Math.sqrt(dx * dx + dy * dy);
        const curveFactor = Math.min(0.3, 30 / dist);
        
        // Create a very gentle curve
        return `M${sx},${sy} Q${sx + dx/2 + dy*curveFactor},${sy + dy/2 - dx*curveFactor} ${tx},${ty}`;
      };

      // Update positions on tick with smoother animation
      this.simulation.on("tick", () => {
        // Keep nodes within the visible area to prevent them from going offscreen
        nodes.forEach(node => {
          node.x = Math.max(30, Math.min(width - 30, node.x || width/2));
          node.y = Math.max(30, Math.min(height - 30, node.y || height/2));
        });
        
        // Update link and node positions
        link.attr("d", linkPath);
        node.attr("transform", d => `translate(${d.x || width/2},${d.y || height/2})`);
      });
    },

    highlightPaths(selectedNode, links, linkElements, nodeElements) {
      if (!selectedNode) {
        // Reset all styles
        linkElements
          .transition()
          .duration(300)
          .style("stroke", "#ccd6e0")
          .style("stroke-width", 2)
          .style("opacity", 1);
          
        nodeElements
          .transition()
          .duration(300)
          .style("opacity", 1);
          
        return;
      }

      // Find all connected nodes and links
      const connectedLinks = new Set();
      const connectedNodes = new Set([selectedNode.id]);

      links.forEach(link => {
        const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
        const targetId = typeof link.target === 'object' ? link.target.id : link.target;
        
        if (sourceId === selectedNode.id || targetId === selectedNode.id) {
          connectedLinks.add(link);
          connectedNodes.add(sourceId);
          connectedNodes.add(targetId);
        }
      });

      // Get color based on node group
      const getColorForGroup = (group) => {
        const colors = {
          'agent': '#48BB78',
          'service': '#4299E1',
          'user': '#805AD5',
          'client': '#F6AD55',
          'server': '#CBD5E0'
        };
        return colors[group] || '#CBD5E0';
      };

      // Update styles with smooth transitions
      linkElements
        .transition()
        .duration(300)
        .style("stroke", d => {
          if (connectedLinks.has(d)) {
            // Get color based on source node group
            const sourceNode = links.find(l => (typeof l.source === 'object' ? l.source.id : l.source) === d.source);
            const sourceGroup = typeof d.source === 'object' ? d.source.group : 
              (sourceNode && sourceNode.source && sourceNode.source.group) || 'default';
            return getColorForGroup(sourceGroup);
          }
          return "#ccd6e0";
        })
        .style("stroke-width", d => connectedLinks.has(d) ? 3 : 1.5)
        .style("opacity", d => connectedLinks.has(d) ? 1 : 0.15)
        .style("filter", d => connectedLinks.has(d) ? "drop-shadow(0px 0px 3px rgba(0, 0, 0, 0.2))" : "none");

      nodeElements
        .transition()
        .duration(300)
        .style("opacity", d => connectedNodes.has(d.id) ? 1 : 0.2)
        .style("filter", d => connectedNodes.has(d.id) ? "drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.2))" : "drop-shadow(0px 1px 2px rgba(0, 0, 0, 0.1))");
        
      // Update node size instead of using transform which can affect positioning
      nodeElements.selectAll("circle")
        .transition()
        .duration(300)
        .attr("r", d => connectedNodes.has(d.id) ? 32 : 28);
    },

    getIconForGroup(group) {
      switch (group) {
        case 'server':
          return 'img/servers.png';
        case 'agent':
          return 'img/global-network.png';
        case 'service':
          return 'img/api.png';
        case 'client':
          return 'img/connect.png';
        default:
          return 'img/user.png';
      }
    }
  }
};
</script>

<style scoped>
.content {
  padding: 20px;
}

.network-card {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  overflow: hidden;
  background-color: #ffffff;
  height: calc(100vh - 140px);
  display: flex;
  flex-direction: column;
}

.network-card .card-body {
  flex: 1;
  overflow: hidden;
  padding: 0;
}

.network-interface {
  height: 100%;
  position: relative;
  display: flex;
}

.graph-container {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
}

#d3-graph {
  width: 100%;
  height: 100%;
}

svg {
  width: 100%;
  height: 100%;
  font-family: 'Arial', sans-serif;
}

.network-down {
  height: 70vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.network-empty {
  padding: 48px;
  text-align: center;
}

.network-empty-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--fm-text);
}

.network-empty-desc {
  font-size: 13px;
  color: var(--fm-slate-500);
  margin-top: 6px;
}

.nodes image,
.nodes text {
  pointer-events: none;
}

.zoom-controls {
  opacity: 0.85;
}

/* ===== Node detail panel ===== */
:deep(.b-sidebar) {
  width: 320px !important;
}

:deep(.b-sidebar-header) {
  background-color: #fff;
  border-bottom: 1px solid var(--fm-border);
  padding: 14px 16px;
}

.fm-node-panel {
  padding: 16px;
}

.fm-node-head {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--fm-border);
  margin-bottom: 4px;
}

.fm-node-icon {
  border-radius: 8px;
}

.fm-node-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--fm-text);
  word-break: break-all;
}

.fm-node-section-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--fm-slate-400);
  margin: 16px 0 4px;
}

.fm-node-conns {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.fm-node-conn {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border: 1px solid var(--fm-border);
  border-radius: 6px;
  background: #fff;
  font-size: 12.5px;
  color: var(--fm-text);
  cursor: pointer;
  text-align: left;
}

.fm-node-conn:hover {
  background: var(--fm-slate-100);
}

.fm-node-foot {
  margin-top: 20px;
}
</style>
